import React, { useState, useEffect, FormEvent, useCallback, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Badge,
  FormField,
  Input,
  Select,
  Checkbox,
  LoadingState,
  EmptyState,
  Modal,
  PageHeader,
} from './ui';
// Servicios
import { createEnsayo, getEnsayos, Ensayo } from '../services/ensayoService';
import { hasPermission, getUserType, getCurrentUser, hasTestManagementPermission } from '../services/authService';
import { ActiveTab } from '../types';
import { getClientes, Cliente, getClienteById, getClienteActual } from '../services/clienteService';
import { createPlanta, getPlantasByClienteId, Planta } from '../services/plantaService';
import { getLaboratorios, getLaboratorioById, Laboratorio } from '../services/laboratorioService';
import { generarProximoIdNea } from '../services/muestraService';
import { getEnsayosMedio, EnsayoMedio } from '../services/ensayoMedioService';
import { getSitiosMuestreo, getSitiosMuestreoPorPlanta, SitioMuestreo } from '../services/sitioMuestreoService';
import { getOrganismosControl, OrganismoControl } from '../services/organismoControlService';
import { guardarAnalisisCompleto } from '../services/muestraService';
import api from '../services/api';

// Tipos de pestañas disponibles
type TabType = 'solicitante' | 'laboratorio' | 'muestra';

// Estructura de muestra única
interface EnsayoData {
  id: number;
  /** Id del ensayo en el catálogo, el que se guarda en la muestra. */
  ensayo_id?: number | null;
  analito: string;
  resultado: string;
  unidad: string;
  medio: string;
  tipo: string;
  // Nuevos campos informativos
  regulado_id?: number | null;
  id_marco?: number | null;
  marco_nombre?: string | null;
  valor_min?: number | null;
  valor_max?: number | null;
  valor_ref?: number | null;
  presencia?: string | null;
  controlado_id?: number | null;
  id_organismo?: number | null;
  organismo_nombre?: string | null;
  metodo_deteccion?: string | null;
  limite_deteccion?: number | null;
  id_ens_control?: number | null; // alias para enviar a backend si controlado
}

interface MuestraData {
  id: number;
  id_nea: string;
  id_m_origen: string;
  id_m_lab: string;
  id_muestra_lab: string;
  responsable: string;
  pertenencia: string;
  sitio_muestreo: string;
  georr_lat: string;
  georr_long: string;
    medio: string;
  fecha_muestreo: string;
  ensayos: EnsayoData[];
  mostrar_organismo_control: boolean;
  organismo_control_id: number | null;
  mostrar_cadena_custodia: boolean;
  cadena_custodia: string;
  remito?: string;
  descripcion?: string;
}

// Define el tipo para FormData
interface FormDataType {
  id_cliente: number;
  id_laboratorio: number;
  id_origen: number;
  fecha_muestreo: string;
  fecha_recepcion: string;
  fecha_analisis: string;
  fecha_emision: string;
  organismo_control: boolean;
  cadena_custodia: boolean;
  muestras: MuestraData[];
  // Campos del solicitante
  solicitante: string;
  cuit_sol: string;
  direccion_sol: string;
  // Campos del laboratorio
  laboratorio: string;
  cuit_lab: string;
  direccion_lab: string;
  // Campos adicionales
  planta: string;
  mostrar_numero_origen: boolean;
  numero_analisis_origen: string;
  mostrar_numero_laboratorio: boolean;
  numero_analisis_laboratorio: string;
}

const defaultFormData: FormDataType = {
  id_cliente: 0,
  id_laboratorio: 0,
  id_origen: 0,
  fecha_muestreo: '',
  fecha_recepcion: '',
  fecha_analisis: '',
  fecha_emision: '',
  organismo_control: false,
  cadena_custodia: false,
  muestras: [],
  // Campos del solicitante
  solicitante: '',
  cuit_sol: '',
  direccion_sol: '',
  // Campos del laboratorio
  laboratorio: '',
  cuit_lab: '',
  direccion_lab: '',
  // Campos adicionales
  planta: '',
  mostrar_numero_origen: false,
  numero_analisis_origen: '',
  mostrar_numero_laboratorio: false,
  numero_analisis_laboratorio: ''
};

// Función para seleccionar cliente actual
const selectClienteActual = async (
  setClientesLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedClienteId: React.Dispatch<React.SetStateAction<number | ''>>,
  setFormData: React.Dispatch<React.SetStateAction<FormDataType>>,
  setPlantas: React.Dispatch<React.SetStateAction<Planta[]>>,
  setSelectedPlantaId: React.Dispatch<React.SetStateAction<number | ''>>,
  setMessage: React.Dispatch<React.SetStateAction<{ text: string; type: 'success' | 'error' } | null>>
) => {
  setClientesLoading(true);
  try {
    // Obtener cliente actual
    const clienteActual = await getClienteActual();
    
    if (clienteActual && clienteActual.id) {
      setSelectedClienteId(clienteActual.id);
      
      // Actualizar formulario con datos del cliente
      setFormData(prev => ({
        ...prev,
        solicitante: clienteActual && clienteActual.razon_social || clienteActual && clienteActual.nombre || '',
        cuit_sol: clienteActual && clienteActual.cuit || '',
        direccion_sol: clienteActual && clienteActual.direccion || ''
      }));
      
      // Cargar plantas asociadas al cliente
      try {
        const plantasData = await getPlantasByClienteId(clienteActual.id);
        setPlantas(plantasData);
        
        // Si hay plantas, seleccionar la primera por defecto
        if (plantasData.length > 0) {
          setSelectedPlantaId(plantasData[0].id || 0);
          setFormData(prev => ({
            ...prev,
            planta: plantasData[0].denominacion || '',
            direccion_sol: plantasData[0].direccion || prev.direccion_sol
          }));
        } else {
          // Si no hay plantas, dejar seleccionada la opción "Ninguna"
          setSelectedPlantaId('');
        }
      } catch (error) {
        console.error('Error al cargar plantas del cliente:', error);
        setMessage({ 
          text: 'Error al cargar plantas del cliente', 
          type: 'error' 
        });
      }
    } else {
      setMessage({ 
        text: 'No se pudo obtener el cliente actual', 
        type: 'error' 
      });
    }
  } catch (error) {
    console.error('Error al cargar cliente actual:', error);
    setMessage({ 
      text: 'Error al cargar información del cliente', 
      type: 'error' 
    });
  } finally {
    setClientesLoading(false);
  }
};

interface BusquedaEnsayoPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEnsayo: (ensayo: any) => void;
  ensayoIndex: number;
  /** ID del medio elegido en Datos de la muestra. */
  medioActual: string;
  medios: EnsayoMedio[];
  /** Sólo un administrador con gestión de ensayos puede dar de alta uno nuevo. */
  puedeCrearEnsayo: boolean;
}

/**
 * Listado de ensayos del catálogo para una muestra. Sólo muestra los del medio
 * elegido en Datos de la muestra: se filtra por el mismo identificador que
 * guarda la muestra, así que la lista no puede quedar fuera de sincronía.
 */
const BusquedaEnsayoPopup: React.FC<BusquedaEnsayoPopupProps> = ({
  isOpen,
  onClose,
  onSelectEnsayo,
  medioActual,
  medios,
  puedeCrearEnsayo,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [nuevoEnsayo, setNuevoEnsayo] = useState({ analito: '', unidad: '', tipo: '' });
  const [guardandoEnsayo, setGuardandoEnsayo] = useState(false);

  const medioId = Number(medioActual);
  const medioValido = !Number.isNaN(medioId) && medioId > 0;
  const nombreMedio = medios.find(m => Number(m.id) === medioId)?.medio || '';

  const formatearEnsayo = useCallback(
    (ensayo: any) => ({
      id: ensayo.id,
      analito: ensayo.analito || 'Sin nombre',
      unidad: ensayo.unidad || '',
      medio: medios.find(m => Number(m.id) === Number(ensayo.medio))?.medio || String(ensayo.medio ?? ''),
      tipo: ensayo.tipo || '',
      regulado_id: ensayo.regulado_id ?? null,
      id_marco: ensayo.id_marco ?? null,
      marco_nombre: ensayo.marco_nombre ?? null,
      valor_min: ensayo.valor_min ?? null,
      valor_max: ensayo.valor_max ?? null,
      valor_ref: ensayo.valor_ref ?? null,
      presencia: ensayo.presencia ?? null,
      controlado_id: ensayo.controlado_id ?? null,
      id_organismo: ensayo.id_organismo ?? null,
      organismo_nombre: ensayo.organismo_nombre ?? null,
      metodo_deteccion: ensayo.metodo_deteccion ?? null,
      limite_deteccion: ensayo.limite_deteccion ?? null,
      id_ens_control: ensayo.controlado_id ?? null,
      resultado: '',
    }),
    [medios]
  );

  const buscarEnsayos = useCallback(async () => {
    if (!medioValido) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const todosEnsayos = (await getEnsayos()) as any[];
      const delMedio = todosEnsayos.filter(ensayo => Number(ensayo.medio) === medioId);
      setResults(delMedio.map(formatearEnsayo));
    } catch (err) {
      console.error('Error al buscar ensayos:', err);
      setError('No se pudo obtener el listado de ensayos. Intente nuevamente.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [medioId, medioValido, formatearEnsayo]);

  // Cargar ensayos al abrir el popup
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setMostrarAlta(false);
      setNuevoEnsayo({ analito: '', unidad: '', tipo: '' });
      buscarEnsayos();
    }
  }, [isOpen, buscarEnsayos]);

  const filtrados = results.filter(ensayo => {
    const texto = searchTerm.trim().toLowerCase();
    if (texto === '') return true;
    return (
      ensayo.analito.toLowerCase().includes(texto) ||
      String(ensayo.tipo).toLowerCase().includes(texto) ||
      String(ensayo.unidad).toLowerCase().includes(texto)
    );
  });

  const handleSelectEnsayo = (ensayo: any) => {
    onSelectEnsayo({ ...ensayo, resultado: ensayo.resultado || '' });
    onClose();
  };

  const handleCrearEnsayo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!medioValido) return;
    setGuardandoEnsayo(true);
    setError(null);
    try {
      // El ensayo nuevo hereda el medio de la muestra, así que aparece siempre
      // dentro del listado desde el que se lo creó.
      const creado = await createEnsayo({
        analito: nuevoEnsayo.analito.trim(),
        unidad: nuevoEnsayo.unidad.trim(),
        tipo: nuevoEnsayo.tipo.trim(),
        medio: medioId,
        estado: 1,
      });
      setMostrarAlta(false);
      setNuevoEnsayo({ analito: '', unidad: '', tipo: '' });
      await buscarEnsayos();
      handleSelectEnsayo(formatearEnsayo(creado));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo crear el ensayo.');
    } finally {
      setGuardandoEnsayo(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal title="Ensayos disponibles para medio seleccionado" onClose={onClose} size="xl">
      <div>
        <p className="mb-4 text-sm text-slate-600">
          {medioValido
            ? `Medio de la muestra: ${nombreMedio || medioId}`
            : 'Seleccione primero el medio en Datos de la muestra.'}
        </p>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar analito, tipo o unidad..."
            className="flex-1 min-w-[12rem]"
            disabled={!medioValido}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={buscarEnsayos}
            icon={<MagnifyingGlassIcon className="h-4 w-4" />}
            disabled={!medioValido}
          >
            Actualizar
          </Button>
          {puedeCrearEnsayo && (
            <Button
              type="button"
              onClick={() => setMostrarAlta(actual => !actual)}
              icon={<PlusIcon className="h-4 w-4" />}
              disabled={!medioValido}
            >
              Agregar ensayo nuevo
            </Button>
          )}
        </div>

        {mostrarAlta && (
          <form
            onSubmit={handleCrearEnsayo}
            className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-brand-200 bg-brand-50 p-4 sm:grid-cols-4"
          >
            <FormField label={<span className="text-xs">Analito *</span>}>
              <Input
                type="text"
                value={nuevoEnsayo.analito}
                onChange={(e) => setNuevoEnsayo(n => ({ ...n, analito: e.target.value }))}
                required
              />
            </FormField>
            <FormField label={<span className="text-xs">Unidad *</span>}>
              <Input
                type="text"
                value={nuevoEnsayo.unidad}
                onChange={(e) => setNuevoEnsayo(n => ({ ...n, unidad: e.target.value }))}
                required
              />
            </FormField>
            <FormField label={<span className="text-xs">Tipo *</span>}>
              <Input
                type="text"
                value={nuevoEnsayo.tipo}
                onChange={(e) => setNuevoEnsayo(n => ({ ...n, tipo: e.target.value }))}
                placeholder="Fisicoquímico, Microbiológico..."
                required
              />
            </FormField>
            <div className="flex items-end gap-2">
              <Button type="submit" size="sm" loading={guardandoEnsayo}>
                Crear y usar
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setMostrarAlta(false)}>
                Cancelar
              </Button>
            </div>
            <p className="sm:col-span-4 text-xs text-slate-600">
              El ensayo se crea para el medio seleccionado y queda disponible para el resto de los análisis.
            </p>
          </form>
        )}

        {loading ? (
          <LoadingState label="Buscando ensayos..." />
        ) : filtrados.length === 0 ? (
          <EmptyState
            title={
              !medioValido
                ? 'Elija un medio para ver los ensayos disponibles'
                : searchTerm
                  ? 'No se encontraron ensayos para este criterio'
                  : 'No hay ensayos disponibles para este medio'
            }
          />
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="min-w-full divide-y divide-slate-200 table-fixed text-left">
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-2/5">Analito</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/6">Unidad</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/6">Medio</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/6">Tipo</th>
                  <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-12">Reg.</th>
                  <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-12">Ctrl.</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/12">Acción</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filtrados.map((ensayo, index) => (
                  <tr key={ensayo.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-4 py-2 text-sm break-words">
                      {ensayo.analito}
                      {/* Detalle breve */}
                      {(ensayo.marco_nombre || ensayo.organismo_nombre) && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          {ensayo.marco_nombre && <span>Marco: {ensayo.marco_nombre}</span>}
                          {ensayo.marco_nombre && ensayo.organismo_nombre && <span> · </span>}
                          {ensayo.organismo_nombre && <span>Org.: {ensayo.organismo_nombre}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{ensayo.unidad}</td>
                    <td className="px-3 py-2 text-xs">{ensayo.medio}</td>
                    <td className="px-3 py-2 text-xs">{ensayo.tipo}</td>
                    <td className="px-2 py-2 text-xs">
                      <Badge tone={ensayo.regulado_id ? 'brand' : 'neutral'}>{ensayo.regulado_id ? 'Sí' : 'No'}</Badge>
                    </td>
                    <td className="px-2 py-2 text-xs">
                      <Badge tone={ensayo.controlado_id ? 'brand' : 'neutral'}>{ensayo.controlado_id ? 'Sí' : 'No'}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Button type="button" size="sm" onClick={() => handleSelectEnsayo(ensayo)}>
                        Seleccionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

interface CrearAnalisisProps {
  onNavigate?: (tab: ActiveTab, id?: string) => void;
}

const CrearAnalisis: React.FC<CrearAnalisisProps> = ({ onNavigate }) => {
  // Verificar si el usuario tiene permiso para crear análisis
  const tienePermisoCrearAnalisis = hasPermission('p_nuevo_analisis');

  // Obtener el tipo de usuario
  const userType = getUserType();
  const isAdmin = userType === 'administrador' || userType === 'administradores';
  const isCliente = userType === 'cliente';
  // El cliente no elige laboratorio: sólo carga sus datos y los de la muestra.
  const eligeLaboratorio = !isCliente;
  const puedeCrearEnsayo = hasTestManagementPermission();
  
  // Estado para controlar la pestaña activa
  const [activeTab, setActiveTab] = useState<TabType>('solicitante');
  
  // Estado para controlar la muestra activa
  const [activeMuestraIndex, setActiveMuestraIndex] = useState<number>(0);

  // Estado para el formulario
  const [formData, setFormData] = useState<FormDataType>({
    id_cliente: 0,
    id_laboratorio: 0,
    id_origen: 0,
    fecha_muestreo: '',
    fecha_recepcion: '',
    fecha_analisis: '',
    fecha_emision: '',
    organismo_control: false,
    cadena_custodia: false,
    muestras: [],
    // Campos del solicitante
    solicitante: '',
    cuit_sol: '',
    direccion_sol: '',
    // Campos del laboratorio
    laboratorio: '',
    cuit_lab: '',
    direccion_lab: '',
    // Campos adicionales
    planta: '',
    mostrar_numero_origen: false,
    numero_analisis_origen: '',
    mostrar_numero_laboratorio: false,
    numero_analisis_laboratorio: ''
  });

  // Estados adicionales
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [plantas, setPlantas] = useState<Planta[]>([]);
  const [selectedClienteId, setSelectedClienteId] = useState<number | ''>('');
  const [selectedPlantaId, setSelectedPlantaId] = useState<number | ''>('');
  const [clientesLoading, setClientesLoading] = useState(false);
  const [plantasLoading, setPlantasLoading] = useState(false);
  const [isLoadingPlantas, setIsLoadingPlantas] = useState(false);
  const [muestras, setMuestras] = useState<MuestraData[]>([
    {
      id: 0,
      id_nea: '',
      id_m_origen: '',
      id_m_lab: '',
      id_muestra_lab: '',
      responsable: '',
      pertenencia: '',
      sitio_muestreo: '',
      georr_lat: '',
      georr_long: '',
      medio: '',
      fecha_muestreo: '',
      ensayos: [],
      mostrar_organismo_control: false,
      organismo_control_id: null,
      mostrar_cadena_custodia: false,
      cadena_custodia: '',
      remito: '',
      descripcion: ''
    }
  ]);
  const [files, setFiles] = useState<File[]>([]);
  // const [observaciones, setObservaciones] = useState(''); // Eliminamos esta línea
  
  // Estado para mensajes
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [dataToSave, setDataToSave] = useState<any>(null);

  // Estado para carga inicial
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estado para archivos
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [firmaFile, setFirmaFile] = useState<File | null>(null);
  const [firmaPreview, setFirmaPreview] = useState<string | null>(null);

  // Estado adicional para tests
  const [tests, setTests] = useState<Ensayo[]>([]);

  // Estado para laboratorios
  const [laboratorios, setLaboratorios] = useState<Laboratorio[]>([]);
  const [selectedLaboratorioId, setSelectedLaboratorioId] = useState<number | ''>('');
  const [laboratoriosLoading, setLaboratoriosLoading] = useState(false);
  
  // Estado para próximo ID-NEA
  const [proximoIdNea, setProximoIdNea] = useState<string>('');
  const [generatingIdNea, setGeneratingIdNea] = useState(false);
  const [errorIdNea, setErrorIdNea] = useState<string>('');

  // Agregar estados para los nuevos datos
  const [medios, setMedios] = useState<EnsayoMedio[]>([]);
  const [sitiosMuestreo, setSitiosMuestreo] = useState<SitioMuestreo[]>([]);
  const [loadingMedios, setLoadingMedios] = useState(false);
  const [loadingSitios, setLoadingSitios] = useState(false);

  // Estado para controlar si se muestran los campos de ID
  const [mostrarCamposId, setMostrarCamposId] = useState<boolean>(false);

  // Agregar después del estado errorIdNea:
  const [popupOpen, setPopupOpen] = useState(false);
  const [editingEnsayoIndex, setEditingEnsayoIndex] = useState<number>(-1);

  // Estado para los analistas (pueden ser múltiples)
  const [analistas, setAnalistas] = useState(['']);
  const [director, setDirector] = useState('');

  // Alta rápida de planta desde Datos del solicitante.
  const [mostrarAltaPlanta, setMostrarAltaPlanta] = useState(false);
  const [nuevaPlanta, setNuevaPlanta] = useState({ denominacion: '', direccion: '' });
  const [guardandoPlanta, setGuardandoPlanta] = useState(false);

  // Aviso de análisis creado: al aceptarlo se vuelve al panel.
  const [analisisCreado, setAnalisisCreado] = useState<string | null>(null);

  const [organismosControl, setOrganismosControl] = useState<OrganismoControl[]>([]);
  const [organismosControlLoading, setOrganismosControlLoading] = useState(false);

  // Añadimos un mapa que asocia nombres de analitos con sus IDs
  const [analitosMap, setAnalitosMap] = useState<Map<string, any>>(new Map());

  const createDefaultMuestra = (): MuestraData => ({
    id: 0,
    id_nea: '',
    id_m_origen: '',
    id_m_lab: '',
    id_muestra_lab: '',
    responsable: '',
    pertenencia: '',
    sitio_muestreo: '',
    georr_lat: '',
    georr_long: '',
    medio: '',
    fecha_muestreo: '',
    ensayos: [],
    mostrar_organismo_control: false,
    organismo_control_id: null,
    mostrar_cadena_custodia: false,
    cadena_custodia: '',
    remito: '',
    descripcion: ''
  });

  const defaultMuestra = createDefaultMuestra();

  // Cargar datos iniciales al montar el componente
  useEffect(() => {
    const cargarDatos = async () => {
      setIsLoading(true);
      try {
        const userType = getUserType();
        
        // Si el usuario es cliente, obtener sus datos automáticamente
        if (userType === 'cliente') {
          const user = getCurrentUser();
          if (user && user.id_grupo) {
            // Obtener datos del cliente actual
            const cliente = await getClienteById(user.id_grupo);
            if (cliente) {
              setFormData(prev => ({
                ...prev,
                solicitante: cliente.nombre || '',
                cuit_sol: cliente.cuit || ''
              }));
              
              // Cargar plantas asociadas al cliente
              if (cliente.id) {
                setIsLoadingPlantas(true);
                try {
                  const plantasCliente = await getPlantasByClienteId(cliente.id);
                  setPlantas(plantasCliente);
                } catch (error) {
                  console.error('Error al cargar plantas:', error);
                } finally {
                  setIsLoadingPlantas(false);
                }
              }
            }
          }
        } else {
          // Cargar clientes normalmente para usuarios no-cliente
          const clientesData = await getClientes();
          setClientes(clientesData);
        }
        
        // Cargar laboratorios
        const laboratoriosData = await getLaboratorios();
        setLaboratorios(laboratoriosData);

        // Cargar ensayos disponibles. El mapa de analitos se usa como respaldo
        // para resolver el id cuando el ensayo viene de una versión anterior.
        const ensayos = await getEnsayos();
        setTests(ensayos);
        const analitosMapa = new Map();
        ensayos.forEach(ensayo => {
          analitosMapa.set(ensayo.analito, { id: ensayo.id });
        });
        setAnalitosMap(analitosMapa);

        // Cargar medios de ensayo
        setLoadingMedios(true);
        try {
          const mediosData = await getEnsayosMedio();
          setMedios(mediosData);
        } catch (error) {
          console.error('Error al cargar medios de ensayo:', error);
        } finally {
          setLoadingMedios(false);
        }

        // Cargar sitios de muestreo
        setLoadingSitios(true);
        try {
          const sitiosData = await getSitiosMuestreo();
          setSitiosMuestreo(sitiosData);
        } catch (error) {
          console.error('Error al cargar sitios de muestreo:', error);
        } finally {
          setLoadingSitios(false);
        }

        // Cargar organismos de control
        const fetchOrganismosControl = async () => {
          setOrganismosControlLoading(true);
          try {
            const token = localStorage.getItem('token');
            if (!token) {
              throw new Error('No hay token de autenticación');
            }

            const response = await fetch(`${api.defaults.baseURL}/organismos-control`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              throw new Error(`Error al cargar organismos de control: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              throw new Error('La respuesta no es JSON válido');
            }

            const data = await response.json();
            setOrganismosControl(data);
          } catch (error) {
            console.error('Error al cargar organismos de control:', error);
            setError('Error al cargar organismos de control');
            // En caso de error, usar datos de ejemplo para desarrollo
            setOrganismosControl([
              { id: 1, organismo: 'Organismo de Control 1' },
              { id: 2, organismo: 'Organismo de Control 2' },
              { id: 3, organismo: 'Organismo de Control 3' }
            ]);
          } finally {
            setOrganismosControlLoading(false);
          }
        };

        fetchOrganismosControl();
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
        setError('Error al cargar datos iniciales');
      } finally {
        setIsLoading(false);
      }
    };

    cargarDatos();
  }, []);

  // Cargar datos del cliente actual si el usuario es cliente
  useEffect(() => {
    if (isCliente && tienePermisoCrearAnalisis) {
      selectClienteActual(
        setClientesLoading,
        setSelectedClienteId,
        setFormData,
        setPlantas,
        setSelectedPlantaId,
        setMessage
      );
    }
  }, [isCliente, tienePermisoCrearAnalisis]);

  // Cargar plantas cuando se selecciona un cliente
  useEffect(() => {
    if (selectedClienteId && tienePermisoCrearAnalisis) {
      const fetchPlantas = async () => {
        setPlantasLoading(true);
        try {
          const plantasData = await getPlantasByClienteId(Number(selectedClienteId));
          setPlantas(plantasData);
        } catch (error) {
          console.error('Error al cargar plantas:', error);
        } finally {
          setPlantasLoading(false);
        }
      };
      
      fetchPlantas();
    } else {
      setPlantas([]);
    }
  }, [selectedClienteId, tienePermisoCrearAnalisis]);

  // Actualizar datos del formulario cuando se selecciona un cliente
  const handleClienteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const clienteId = e.target.value;
    setSelectedClienteId(clienteId === '' ? '' : Number(clienteId));
    setSelectedPlantaId('');
    
    if (clienteId === '') {
      // Limpiar los campos si no se selecciona cliente
      setFormData({
        ...formData,
        solicitante: '',
        cuit_sol: '',
        direccion_sol: '',
        planta: ''
      });
    } else {
      // Buscar el cliente seleccionado
      const cliente = clientes.find(c => c.id === Number(clienteId));
      if (cliente) {
        setFormData(prev => ({
          ...prev,
          solicitante: cliente.razon_social || cliente.nombre,
          cuit_sol: cliente.cuit || '',
          direccion_sol: cliente.direccion || ''
        }));
      }
    }
  };

  // Actualizar planta cuando se selecciona una
  const handlePlantaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const plantaId = e.target.value;
    setSelectedPlantaId(plantaId === '' ? '' : Number(plantaId));
    
    if (plantaId === '') {
      setFormData(prev => ({
        ...prev,
        planta: ''
      }));
    } else {
      // Buscar la planta seleccionada
      const planta = plantas.find(p => p.id === Number(plantaId));
      if (planta) {
        setFormData(prev => ({
          ...prev,
          planta: planta.denominacion || ''
        }));
      }
    }
  };

  // Alta de una planta para el cliente seleccionado. El backend admite una sola
  // planta por cliente, así que la opción sólo aparece cuando todavía no tiene.
  const handleCrearPlanta = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClienteId) return;
    setGuardandoPlanta(true);
    try {
      const creada = await createPlanta({
        id_cliente: Number(selectedClienteId),
        denominacion: nuevaPlanta.denominacion.trim(),
        direccion: nuevaPlanta.direccion.trim(),
      });
      if (!creada) {
        setMessage({ text: 'No se pudo crear la planta', type: 'error' });
        return;
      }
      const plantasActualizadas = await getPlantasByClienteId(Number(selectedClienteId));
      setPlantas(plantasActualizadas);
      setSelectedPlantaId(creada.id || '');
      setFormData(prev => ({ ...prev, planta: creada.denominacion || '' }));
      setMostrarAltaPlanta(false);
      setNuevaPlanta({ denominacion: '', direccion: '' });
    } catch (error) {
      console.error('Error al crear la planta:', error);
      setMessage({ text: 'No se pudo crear la planta', type: 'error' });
    } finally {
      setGuardandoPlanta(false);
    }
  };

  // Obtener la muestra activa
  const activeMuestra = muestras[activeMuestraIndex];

  // Mensaje de advertencia si el usuario no tiene permiso
  const mensajePermiso = !tienePermisoCrearAnalisis && (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-amber-700">
            No tienes permiso para crear análisis. Los campos han sido deshabilitados.
          </p>
        </div>
      </div>
    </div>
  );

  // Manejadores de cambios
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!tienePermisoCrearAnalisis) return;
    
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Manejar cambios en los datos de la muestra
  const handleMuestraChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!tienePermisoCrearAnalisis) return;
    
    const { name, value } = e.target;
    
    // Si el campo que cambió es el medio y ya hay ensayos, mostrar alerta
    if (name === 'medio' && muestras[activeMuestraIndex].ensayos.length > 0 && 
        muestras[activeMuestraIndex].ensayos[0].analito !== '') {
      
      const confirmar = window.confirm(
        "Cambiar el medio eliminará todos los ensayos asociados a esta muestra. ¿Desea continuar?"
      );
      
      if (confirmar) {
        // Si el usuario confirma, actualizamos el medio y eliminamos los ensayos
        setMuestras(prev => {
          const updated = [...prev];
          updated[activeMuestraIndex] = {
            ...updated[activeMuestraIndex],
            [name]: value,
            ensayos: [{ id: 1, analito: '', resultado: '', unidad: '', medio: '', tipo: '' }]
          };
          return updated;
        });
      }
      return; // No hacemos nada si el usuario cancela
    }
    
    // Para otros campos o si no hay ensayos, actualizamos normalmente
    setMuestras(prev => {
      const updated = [...prev];
      updated[activeMuestraIndex] = {
        ...updated[activeMuestraIndex],
        [name]: value
      };
      return updated;
    });
  };

  // Manejar cambios en los ensayos
  const handleEnsayoChange = (index: number, field: string, value: string) => {
    if (!tienePermisoCrearAnalisis) return;
    
    setMuestras(prev => {
      const updated = [...prev];
      const updatedEnsayos = [...updated[activeMuestraIndex].ensayos];
      updatedEnsayos[index] = { ...updatedEnsayos[index], [field]: value };
      updated[activeMuestraIndex] = {
        ...updated[activeMuestraIndex],
        ensayos: updatedEnsayos
      };
      return updated;
    });
  };

  // Añadir y eliminar ensayos
  const addEnsayo = () => {
    if (!tienePermisoCrearAnalisis) return;
    
    setMuestras(prev => {
      const updated = [...prev];
      const currentEnsayos = [...updated[activeMuestraIndex].ensayos];
      updated[activeMuestraIndex] = {
        ...updated[activeMuestraIndex],
        ensayos: [...currentEnsayos, { 
          id: currentEnsayos.length + 1, 
          analito: '', 
          resultado: '', 
          unidad: '', 
          medio: '', 
          tipo: '' 
        }]
      };
      return updated;
    });
  };

  const removeEnsayo = () => {
    if (!tienePermisoCrearAnalisis) return;
    
    setMuestras(prev => {
      const updated = [...prev];
      const currentEnsayos = [...updated[activeMuestraIndex].ensayos];
      if (currentEnsayos.length > 1) {
        updated[activeMuestraIndex] = {
          ...updated[activeMuestraIndex],
          ensayos: currentEnsayos.slice(0, -1)
        };
      }
      return updated;
    });
  };

  // Añadir muestra al formulario
  const addMuestra = () => {
    // Generar nuevo ID incremental basado en el número de muestras
    const baseId = 1; // Se podría cambiar si hay múltiples series
    const secuencia = muestras.length + 1;
    const nuevoId = `${baseId}/${secuencia}`;
    
    const nuevaMuestra: MuestraData = {
      id: muestras.length, // Añadir el ID
      // Campos de ID con nombres compatibles
      id_nea: nuevoId,
      id_m_origen: '',
      id_m_lab: '',
      id_muestra_lab: '',
      responsable: '',
      pertenencia: '',
      sitio_muestreo: '',
      georr_lat: '',
      georr_long: '',
      medio: '',
      fecha_muestreo: '',
      ensayos: [
        { id: 1, analito: '', resultado: '', unidad: '', medio: '', tipo: '' }
      ],
      mostrar_organismo_control: false,
      organismo_control_id: null,
      mostrar_cadena_custodia: false,
      cadena_custodia: '',
      remito: '',
      descripcion: ''
    };
    
    setMuestras([...muestras, nuevaMuestra]);
    setActiveMuestraIndex(muestras.length);
  };

  // Remover muestra del formulario
  const removeMuestra = (index: number) => {
    if (muestras.length <= 1) return;

    setMuestras(prevMuestras => {
      // Eliminar la muestra del array
      const updatedMuestras = prevMuestras.filter((_, i) => i !== index);
      
      // Renumerar las muestras restantes
      return updatedMuestras.map((muestra, i) => {
        const baseId = 1;
        const secuencia = i + 1;
        const nuevoId = `${baseId}/${secuencia}`;
        
        return {
          ...muestra,
          id_nea: nuevoId,
          id_m_lab: '', // Mantener el ID de laboratorio vacío
          id_muestra_lab: '' // Mantener el ID de laboratorio vacío
        };
      });
    });

    // Ajustar el índice activo
    if (index === activeMuestraIndex) {
      // Si la muestra eliminada era la activa, seleccionar la anterior o la primera
      setActiveMuestraIndex(index === 0 ? 0 : index - 1);
    } else if (index < activeMuestraIndex) {
      // Si la muestra eliminada estaba antes de la activa, ajustar el índice
      setActiveMuestraIndex(activeMuestraIndex - 1);
    }
  };

  // Manejar confirmación
  const handleConfirmClick = () => {
    setShowConfirmModal(true);
  };

  // Cambiar pestaña
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClienteId) {
      setMessage({ text: 'Debe seleccionar un cliente', type: 'error' });
      return;
    }
    
    if (eligeLaboratorio && !selectedLaboratorioId) {
      setMessage({ text: 'Debe seleccionar un laboratorio', type: 'error' });
      return;
    }

    // Sin ensayos no hay nada que analizar.
    const sinEnsayos = muestras.filter(
      muestra => muestra.ensayos.filter(ensayo => ensayo.analito.trim() !== '').length === 0
    );
    if (sinEnsayos.length > 0) {
      const posiciones = sinEnsayos.map(muestra => muestras.indexOf(muestra) + 1).join(', ');
      setMessage({
        text:
          muestras.length === 1
            ? 'Agregue al menos un ensayo a la muestra antes de guardar el análisis.'
            : `Agregue al menos un ensayo a la muestra ${posiciones} antes de guardar el análisis.`,
        type: 'error',
      });
      setActiveTab('muestra');
      return;
    }
      
    // El ID-NEA de la muestra se arma con el prefijo del laboratorio, así que no
    // existe todavía cuando el cliente pide el análisis sin uno asignado: se
    // exigen sólo los datos que el solicitante puede completar.
    const muestrasInvalidas = muestras.some(muestra =>
      !muestra.fecha_muestreo || !muestra.responsable
    );

    if (muestrasInvalidas) {
        setMessage({
        text: 'Todas las muestras deben tener fecha de muestreo y responsable',
          type: 'error'
        });
        return;
      }
      
    // Preparar los datos para el modal de confirmación
    const currentUser = getCurrentUser();
    const laboratorio = selectedLaboratorioId
      ? laboratorios.find(lab => lab.id === Number(selectedLaboratorioId))
      : undefined;
    const cliente = clientes.find(c => c.id === Number(selectedClienteId));
    
    const dataToSaveObj = {
      solicitante: {
        nombre: cliente?.nombre || cliente?.razon_social || formData.solicitante,
        cuit: cliente?.cuit || formData.cuit_sol,
        direccion: formData.direccion_sol
      },
      laboratorio: {
        nombre: laboratorio?.nombre || formData.laboratorio || 'Lo asigna el administrador',
        cuit: laboratorio?.cuit || formData.cuit_lab,
        direccion: laboratorio?.direccion || formData.direccion_lab
      },
      planta: formData.planta,
      fecha_solicitud: formData.fecha_analisis || new Date().toISOString().split('T')[0],
      usuario: currentUser?.username || 'Usuario desconocido',
      muestras: muestras.map(muestra => ({
        id_nea: muestra.id_nea,
        sitio: muestra.sitio_muestreo,
        fecha: muestra.fecha_muestreo,
        responsable: muestra.responsable,
        medio: muestra.medio,
        ensayos: muestra.ensayos.map(ensayo => ensayo.analito).join(', ')
      })),
      // observaciones: observaciones, // Eliminamos esta línea
      idOrigen: formData.mostrar_numero_origen ? formData.numero_analisis_origen : 'No especificado',
      idLab: formData.mostrar_numero_laboratorio ? formData.numero_analisis_laboratorio : 'No especificado'
    };
    
    setDataToSave(dataToSaveObj);
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    
    try {
      // Preparar datos como JSON para el endpoint completo de análisis
      const currentUser = getCurrentUser();
      const userType = getUserType();
      
      // Determinar el ID de origen correcto
      let idOrigenCorreto;
      if (userType === 'cliente') {
        // Si es cliente, usar su propio ID como origen
        idOrigenCorreto = currentUser?.id_grupo || currentUser?.id || parseInt(selectedClienteId.toString());
      } else {
        // Si es admin o laboratorio, usar el cliente seleccionado
        idOrigenCorreto = parseInt(selectedClienteId.toString());
      }
      
      // Preparar datos para el endpoint completo que incluye muestras
        const dataParaGuardar = {
        analisis: {
          id_origen: idOrigenCorreto,
          // Sin laboratorio elegido queda en 0: el administrador lo asigna
          // desde Análisis pendientes antes de que se carguen los resultados.
          id_lab: selectedLaboratorioId ? parseInt(selectedLaboratorioId.toString()) : 0,
          id_planta: selectedPlantaId ? parseInt(selectedPlantaId.toString()) : -1,
          usuario_generador: currentUser?.id || 1,
          id_a_cliente: formData.mostrar_numero_origen ? formData.numero_analisis_origen : '',
          id_a_lab: formData.mostrar_numero_laboratorio ? formData.numero_analisis_laboratorio : '',
          analistas: analistas.filter(a => a.trim()).join(', '),
          director: director,
          titulo: '',
          firma: '',
          logo: ''
        },
          muestras: muestras.map((muestra, indice) => {
            return {
            // Numeración provisoria mientras no hay laboratorio asignado.
            id_nea: muestra.id_nea || `${indice + 1}`,
            id_m_origen: muestra.id_m_origen || '',
            id_m_lab: muestra.id_m_lab || '',
            sitio_muestreo: muestra.sitio_muestreo,
            georr_lat: muestra.georr_lat || '0',
            georr_long: muestra.georr_long || '0',
            medio: muestra.medio,
              fecha_muestreo: muestra.fecha_muestreo,
              responsable: muestra.responsable,
            pertenencia: muestra.pertenencia || '',
              observaciones: muestra.descripcion || '',
            organismo: muestra.mostrar_organismo_control ? (muestra.organismo_control_id?.toString() || '') : '',
            custodia: muestra.mostrar_cadena_custodia ? muestra.cadena_custodia : '',
              ensayos: muestra.ensayos
                .filter(ensayo => ensayo.analito.trim() !== '')
                .map(ensayo => ({
                  // El id llega con el ensayo elegido; el mapa por nombre queda
                  // como respaldo. El método lo carga el laboratorio después.
                  id_ensayo: ensayo.ensayo_id || analitosMap.get(ensayo.analito)?.id || 0,
                  valor: ensayo.resultado || '0',
                  unidad: ensayo.unidad || '',
                  metodo_ensayo: '',
                  limite_deteccion: '0'
                }))
            };
        }),
        sitios: [] // Se crearán automáticamente en el backend
      };
      
      console.log('Datos a enviar:', dataParaGuardar);
      console.log('ID de origen usado:', idOrigenCorreto, 'Tipo de usuario:', userType);
      
      // Enviar los datos al endpoint completo del backend
      const response = await api.post('/analisis/completo', dataParaGuardar);
      
      const data = response.data;

      setMessage(null);
      setAnalisisCreado(data?.analisis?.id_a_nea || (data?.id ? `NEA/${data.id}` : ''));
      
      // Restablecer formulario
      setFormData(defaultFormData);
      setMuestras([createDefaultMuestra()]);
      setActiveMuestraIndex(0);
      setSelectedClienteId('');
      setSelectedLaboratorioId('');
      setSelectedPlantaId('');
      setProximoIdNea('');
      setFiles([]);
      // setObservaciones(''); // Eliminamos esta línea
      setAnalistas(['']);
      setDirector('');
      
      // Cerrar modal
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error al guardar análisis:', error);
      setMessage({
        text: 'Error al guardar el análisis: ' + (error instanceof Error ? error.message : 'Error desconocido'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Agregar el modal de confirmación
  const renderConfirmModal = () => {
    if (!showConfirmModal || !dataToSave) return null;
    
    const confirmFooter = (
      <div className="flex justify-end space-x-3">
        <Button type="button" variant="outline" onClick={() => setShowConfirmModal(false)}>
          Cancelar
        </Button>
        <Button type="button" loading={loading} onClick={handleConfirmSave}>
          Confirmar
        </Button>
      </div>
    );

    return (
      <Modal title="Confirmar creación de análisis" onClose={() => setShowConfirmModal(false)} footer={confirmFooter} size="lg">
        <div>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold mb-2">Solicitante</h3>
                <p><span className="font-medium">Nombre:</span> {dataToSave.solicitante.nombre}</p>
                <p><span className="font-medium">CUIT:</span> {dataToSave.solicitante.cuit}</p>
                <p><span className="font-medium">Dirección:</span> {dataToSave.solicitante.direccion}</p>
                {dataToSave.planta && <p><span className="font-medium">Planta:</span> {dataToSave.planta}</p>}
              </div>

              <div>
                <h3 className="font-bold mb-2">Laboratorio</h3>
                <p><span className="font-medium">Nombre:</span> {dataToSave.laboratorio.nombre}</p>
                <p><span className="font-medium">CUIT:</span> {dataToSave.laboratorio.cuit}</p>
                <p><span className="font-medium">Dirección:</span> {dataToSave.laboratorio.direccion}</p>
              </div>
              
              <div>
                <h3 className="font-bold mb-2">Información general</h3>
                <p><span className="font-medium">Fecha de solicitud:</span> {dataToSave.fecha_solicitud}</p>
                <p><span className="font-medium">Usuario:</span> {dataToSave.usuario}</p>
                <p><span className="font-medium">ID Análisis origen:</span> {dataToSave.idOrigen}</p>
                <p><span className="font-medium">ID Análisis laboratorio:</span> {dataToSave.idLab}</p>
              </div>
              
              <div>
                <h3 className="font-bold mb-2">Muestras ({dataToSave.muestras.length})</h3>
                <div className="max-h-60 overflow-y-auto border rounded-md p-2">
                  {dataToSave.muestras.map((muestra: any, index: number) => (
                    <div key={index} className="mb-3 pb-3 border-b last:border-b-0">
                      <p className="font-medium">Muestra {index + 1}</p>
                      <p>Sitio: {muestra.sitio}</p>
                      <p>Fecha: {muestra.fecha}</p>
                      <p>Responsable: {muestra.responsable}</p>
                      <p>Medio: {muestra.medio}</p>
                      <p>Ensayos: {muestra.ensayos}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Eliminamos el bloque de observaciones generales
              {dataToSave.observaciones && (
                <div>
                  <h4 className="font-medium mb-2">Observaciones</h4>
                  <p>{dataToSave.observaciones}</p>
                </div>
              )}
              */}
            </div>
        </div>
      </Modal>
    );
  };

  // Manejar cambio de logo
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!tienePermisoCrearAnalisis) return;
    
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setLogoFile(selectedFile);
      
      // Crear una URL para previsualizar la imagen
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  // Manejar cambio de firma
  const handleFirmaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!tienePermisoCrearAnalisis) return;
    
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFirmaFile(selectedFile);
      
      // Crear una URL para previsualizar la imagen
      const reader = new FileReader();
      reader.onloadend = () => {
        setFirmaPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  /**
   * Maneja el cambio de laboratorio seleccionado
   */
  const handleLaboratorioChange = (laboratorioId: string) => {
    if (!laboratorioId) return;
    
    const labId = Number(laboratorioId);
    
    // Actualiza el estado con el nuevo laboratorio seleccionado
    setSelectedLaboratorioId(labId);
    
    // Busca el laboratorio seleccionado
    const laboratorio = laboratorios.find(lab => Number(lab.id) === labId);
    
    if (laboratorio) {
      // Actualiza los datos del laboratorio
      setFormData(prev => ({
        ...prev,
        id_laboratorio: laboratorio.id,
        nombre_laboratorio: laboratorio.nombre,
        cuit_laboratorio: laboratorio.cuit || '',
        direccion_laboratorio: laboratorio.direccion || '',
        cuit_lab: laboratorio.cuit || '',
        direccion_lab: laboratorio.direccion || '',
        laboratorio: laboratorio.nombre
      }));
      
      // Llama a la función para generar un nuevo ID-NEA
      // Pasamos directamente el ID del laboratorio en lugar de usar el estado
      generarIdNeaParaLaboratorio(labId);
    }
  };

  /**
   * Función auxiliar para generar un ID-NEA para un laboratorio específico y actualizar las muestras
   */
  const generarIdNeaParaLaboratorio = async (labId: number) => {
    try {
      setGeneratingIdNea(true);
      setErrorIdNea('');
      
      // Usar la función del servicio directamente con el ID proporcionado
      const nuevoIdNea = await generarProximoIdNea(labId);
      
      // Actualizar el estado con el nuevo ID-NEA
      setProximoIdNea(nuevoIdNea);
      
      // Actualizar también los IDs de las muestras
      setMuestras(prevMuestras => {
        return prevMuestras.map((muestra, index) => {
          // El primer número del ID-NEA es el prefijo del laboratorio, lo mantenemos
          // El segundo número es el contador secuencial, lo incrementamos por cada muestra
          const idPartes = nuevoIdNea.split('/');
          const nuevaSecuencia = Number(idPartes[1]) + index;
          const nuevoId = `${idPartes[0]}/${nuevaSecuencia}`;
          
          return {
            ...muestra,
            id_nea: nuevoId,
            id_m_lab: '', // Mantener el ID de laboratorio vacío
            id_muestra_lab: '' // Mantener el ID de laboratorio vacío
          };
        });
      });
    } catch (error: any) {
      console.error('Error al generar el ID-NEA:', error);
      setErrorIdNea(error.message || 'No se pudo generar el ID-NEA. Por favor, inténtelo de nuevo.');
    } finally {
      setGeneratingIdNea(false);
    }
  };

  /**
   * Genera un nuevo ID-NEA basado en el laboratorio seleccionado (usando el estado actual)
   */
  const generarNuevoIdNea = async () => {
    if (!selectedLaboratorioId) {
      setErrorIdNea('Debe seleccionar un laboratorio primero');
      setActiveTab('laboratorio');
      return;
    }

    try {
      setGeneratingIdNea(true);
      setErrorIdNea('');
      
      // Usar la función del servicio directamente con el ID proporcionado
      const nuevoIdNea = await generarProximoIdNea(Number(selectedLaboratorioId));
      
      // Actualizar el estado con el nuevo ID-NEA
      setProximoIdNea(nuevoIdNea);
      
      // Actualizar también los IDs de las muestras
      setMuestras(prevMuestras => {
        return prevMuestras.map((muestra, index) => {
          // El primer número del ID-NEA es el prefijo del laboratorio, lo mantenemos
          // El segundo número es el contador secuencial, lo incrementamos por cada muestra
          const idPartes = nuevoIdNea.split('/');
          const nuevaSecuencia = Number(idPartes[1]) + index;
          const nuevoId = `${idPartes[0]}/${nuevaSecuencia}`;
          
          return {
            ...muestra,
            id_nea: nuevoId,
            id_m_lab: '', // Mantener el ID de laboratorio vacío
            id_muestra_lab: '' // Mantener el ID de laboratorio vacío
          };
        });
      });
    } catch (error: any) {
      console.error('Error al generar el ID-NEA:', error);
      setErrorIdNea(error.message || 'No se pudo generar el ID-NEA. Por favor, inténtelo de nuevo.');
    } finally {
      setGeneratingIdNea(false);
    }
  };

  // Agregar efecto para cargar sitios de muestreo cuando cambia la planta seleccionada
  useEffect(() => {
    if (selectedPlantaId) {
      const cargarSitiosMuestreo = async () => {
        setLoadingSitios(true);
        try {
          const sitiosData = await getSitiosMuestreoPorPlanta(Number(selectedPlantaId));
          setSitiosMuestreo(sitiosData);
        } catch (error) {
          console.error('Error al cargar sitios de muestreo:', error);
        } finally {
          setLoadingSitios(false);
        }
      };
      
      cargarSitiosMuestreo();
    } else {
      // Si no hay planta seleccionada, cargar todos los sitios
      const cargarTodosSitios = async () => {
        setLoadingSitios(true);
        try {
          const sitiosData = await getSitiosMuestreo();
          setSitiosMuestreo(sitiosData);
        } catch (error) {
          console.error('Error al cargar sitios de muestreo:', error);
        } finally {
          setLoadingSitios(false);
        }
      };
      
      cargarTodosSitios();
    }
  }, [selectedPlantaId]);

  // Manejar cambio de sitio de muestreo
  const handleSitioMuestreoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!tienePermisoCrearAnalisis) return;
    
    const sitioId = e.target.value;
    
    if (sitioId) {
      // Encontrar el sitio seleccionado
      const sitio = sitiosMuestreo.find(s => s.id.toString() === sitioId);
      
      if (sitio) {
        // Actualizar datos de la muestra con las coordenadas del sitio
        setMuestras(prev => {
          const updated = [...prev];
          updated[activeMuestraIndex] = {
            ...updated[activeMuestraIndex],
            sitio_muestreo: sitio.denominacion,
            georr_lat: sitio.georreferencia_lat.toString(),
            georr_long: sitio.georreferencia_long.toString()
          };
          return updated;
        });
      }
    } else {
      // Si no se selecciona sitio, limpiar los campos
      setMuestras(prev => {
        const updated = [...prev];
        updated[activeMuestraIndex] = {
          ...updated[activeMuestraIndex],
          sitio_muestreo: '',
          georr_lat: '',
          georr_long: ''
        };
        return updated;
      });
    }
  };

  // Agregar una función para editar un ensayo específico:
  const handleEditEnsayo = (index: number) => {
    setEditingEnsayoIndex(index);
    setPopupOpen(true);
  };

  // Agregar una función para manejar la selección de un ensayo desde el popup:
  const handleSelectEnsayo = (ensayoData: any) => {
    if (editingEnsayoIndex === -1) return;
    
    setMuestras((prev: any[]) => {
      const updated = [...prev];
      const updatedEnsayos = [...updated[activeMuestraIndex].ensayos];
      updatedEnsayos[editingEnsayoIndex] = { 
        ...updatedEnsayos[editingEnsayoIndex], 
        ensayo_id: ensayoData.id,
        analito: ensayoData.analito,
        unidad: ensayoData.unidad,
        medio: ensayoData.medio,
        tipo: ensayoData.tipo,
        resultado: ensayoData.resultado || '',
        regulado_id: ensayoData.regulado_id || null,
        id_marco: ensayoData.id_marco || null,
        marco_nombre: ensayoData.marco_nombre || null,
        valor_min: ensayoData.valor_min || null,
        valor_max: ensayoData.valor_max || null,
        valor_ref: ensayoData.valor_ref || null,
        presencia: ensayoData.presencia || null,
        controlado_id: ensayoData.controlado_id || null,
        id_organismo: ensayoData.id_organismo || null,
        organismo_nombre: ensayoData.organismo_nombre || null,
        metodo_deteccion: ensayoData.metodo_deteccion || null,
        limite_deteccion: ensayoData.limite_deteccion || null,
        id_ens_control: ensayoData.id_ens_control || null
      } as any;
      updated[activeMuestraIndex] = {
        ...updated[activeMuestraIndex],
        ensayos: updatedEnsayos
      };
      return updated;
    });
  };

  // Renderizar pestañas
  const renderTabs = () => {
    const tabs: { key: TabType; label: string }[] = [
      { key: 'solicitante', label: 'Solicitante (Cliente)' },
      ...(eligeLaboratorio ? [{ key: 'laboratorio' as TabType, label: 'Laboratorio' }] : []),
      { key: 'muestra', label: 'Muestras' },
    ];

    return (
      <div className="flex flex-wrap gap-1.5 mb-4">
        {tabs.map(({ key, label }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none
                ${
                  isActive
                    ? 'bg-brand-50 text-brand-800 border border-brand-200'
                    : 'text-slate-600 border border-transparent hover:bg-slate-100 hover:text-slate-800'
                }`}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  // Renderizar contenido según la pestaña activa
  const renderTabContent = () => {
    switch (activeTab) {
      case 'solicitante':
        return (
          <div className="space-y-6 p-6 bg-white rounded-md shadow-sm">
            <h3 className="text-lg font-medium leading-6 text-slate-900 border-b border-slate-200 pb-2">Datos del Solicitante</h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-4">
                {isAdmin ? (
                  <FormField label="Cliente" required>
                    <Select
                      name="cliente_id"
                      value={selectedClienteId}
                      onChange={handleClienteChange}
                      disabled={!tienePermisoCrearAnalisis || clientesLoading || isCliente}
                      required
                    >
                      <option value="">Seleccionar cliente</option>
                      {clientes.map(cliente => (
                        <option key={cliente.id} value={cliente.id}>
                          {cliente.razon_social || cliente.nombre}
                        </option>
                      ))}
                    </Select>
                    {clientesLoading && <span className="text-sm text-slate-500 mt-1">Cargando clientes...</span>}
                  </FormField>
                ) : (
                  <FormField label="Cliente">
                    <Input
                      type="text"
                      value={formData.solicitante}
                      className="bg-slate-100"
                      disabled={true}
                      readOnly
                    />
                  </FormField>
                )}

                <FormField label="Planta">
                  <Select
                    name="planta_id"
                    value={selectedPlantaId}
                    onChange={handlePlantaChange}
                    disabled={!tienePermisoCrearAnalisis || plantasLoading || !selectedClienteId}
                  >
                    <option value="">Sin especificar</option>
                    {plantas.map(planta => (
                      <option key={planta.id} value={planta.id}>
                        {planta.denominacion}
                      </option>
                    ))}
                  </Select>
                  {plantasLoading && <span className="text-sm text-slate-500 mt-1">Cargando plantas...</span>}

                  {/* El solicitante puede dar de alta su planta sin salir del formulario. */}
                  {tienePermisoCrearAnalisis && !!selectedClienteId && !mostrarAltaPlanta && (
                    <button
                      type="button"
                      className="mt-2 text-sm text-brand-700 hover:underline"
                      onClick={() => setMostrarAltaPlanta(true)}
                    >
                      Agregar planta
                    </button>
                  )}

                  {mostrarAltaPlanta && (
                    <div className="mt-3 rounded-md border border-brand-200 bg-brand-50 p-3 space-y-3">
                      <FormField label={<span className="text-xs">Denominación *</span>}>
                        <Input
                          type="text"
                          value={nuevaPlanta.denominacion}
                          onChange={(e) => setNuevaPlanta(planta => ({ ...planta, denominacion: e.target.value }))}
                          placeholder="Nombre de la planta"
                        />
                      </FormField>
                      <FormField label={<span className="text-xs">Dirección</span>}>
                        <Input
                          type="text"
                          value={nuevaPlanta.direccion}
                          onChange={(e) => setNuevaPlanta(planta => ({ ...planta, direccion: e.target.value }))}
                          placeholder="Dirección de la planta"
                        />
                      </FormField>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          loading={guardandoPlanta}
                          disabled={nuevaPlanta.denominacion.trim() === ''}
                          onClick={handleCrearPlanta}
                        >
                          Guardar planta
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setMostrarAltaPlanta(false)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </FormField>

                <FormField label="Dirección">
                  <Input
                    type="text"
                    name="direccion_sol"
                    value={formData.direccion_sol}
                    onChange={handleChange}
                    className="bg-slate-100"
                    placeholder="Se completa automáticamente"
                    disabled={true}
                    readOnly
                  />
                </FormField>

                <FormField label="CUIT">
                  <Input
                    type="text"
                    name="cuit_sol"
                    value={formData.cuit_sol}
                    onChange={handleChange}
                    className="bg-slate-100"
                    placeholder="Se completa automáticamente"
                    disabled={true}
                    readOnly
                  />
                </FormField>

              <div className="sm:col-span-2">
                <Checkbox
                  label="Existe número de análisis de origen"
                  name="mostrar_numero_origen"
                  checked={formData.mostrar_numero_origen}
                  onChange={handleChange}
                />
                {formData.mostrar_numero_origen && (
                  <FormField label="Número de análisis de origen" className="mt-2">
                    <Input
                      type="text"
                      name="numero_analisis_origen"
                      value={formData.numero_analisis_origen}
                      onChange={handleChange}
                      placeholder="Ingrese el número de análisis de origen"
                    />
                  </FormField>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
              <Button
                type="button"
                onClick={() => handleTabChange(eligeLaboratorio ? 'laboratorio' : 'muestra')}
                disabled={loading || !selectedClienteId}
              >
                Siguiente
              </Button>
            </div>
          </div>
        );

      case 'laboratorio':
        return (
          <div className="space-y-6 p-6 bg-white rounded-md shadow-sm">
            <h3 className="text-lg font-medium leading-6 text-slate-900 border-b border-slate-200 pb-2">Datos del Laboratorio</h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-4">
              <FormField label="Laboratorio" required error={errorIdNea}>
                <Select
                  name="laboratorio"
                  value={selectedLaboratorioId || ''}
                  onChange={(e) => handleLaboratorioChange(e.target.value)}
                  disabled={loading || !tienePermisoCrearAnalisis}
                  hasError={!!errorIdNea}
                >
                  <option value="">Seleccionar laboratorio</option>
                  {laboratorios.map((lab) => (
                    <option key={lab.id} value={lab.id}>
                      {lab.nombre}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="CUIT">
                <Input
                  type="text"
                  name="cuit_lab"
                  className="bg-slate-100"
                  value={formData.cuit_lab || ''}
                  onChange={handleChange}
                  disabled={true}
                  readOnly
                />
              </FormField>

              <FormField label="Dirección" className="sm:col-span-2">
                <Input
                  type="text"
                  name="direccion_lab"
                  className="bg-slate-100"
                  value={formData.direccion_lab || ''}
                  onChange={handleChange}
                  disabled={true}
                  readOnly
                />
              </FormField>

              {/* Mover el check de número de análisis de laboratorio al final */}
              <div className="sm:col-span-2 mt-4">
                <Checkbox
                  label="Existe número de análisis de laboratorio"
                  name="mostrar_numero_laboratorio"
                  checked={formData.mostrar_numero_laboratorio}
                  onChange={handleChange}
                />
                {formData.mostrar_numero_laboratorio && (
                  <FormField label="Número de análisis de laboratorio" className="mt-2">
                    <Input
                      type="text"
                      name="numero_analisis_laboratorio"
                      value={formData.numero_analisis_laboratorio}
                      onChange={handleChange}
                      placeholder="Ingrese el número de análisis de laboratorio"
                    />
                  </FormField>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleTabChange('solicitante')}
                disabled={loading}
              >
                Anterior
              </Button>
              <Button
                type="button"
                onClick={() => handleTabChange('muestra')}
                disabled={loading || !selectedLaboratorioId}
              >
                Siguiente
              </Button>
            </div>
          </div>
        );

      case 'muestra':
        return (
          <div className="space-y-6 p-6 bg-white rounded-md shadow-sm">
            <h3 className="text-lg font-medium leading-6 text-slate-900 border-b border-slate-200 pb-2">Datos de la Muestra</h3>

            {/* Controles para añadir/eliminar muestras */}
            <div className="flex justify-end space-x-2 mb-4">
              <Button
                type="button"
                variant="danger"
                icon={<TrashIcon className="h-4 w-4" />}
                onClick={() => removeMuestra(activeMuestraIndex)}
                disabled={muestras.length <= 1}
              >
                Eliminar Muestra
              </Button>
              <Button
                type="button"
                icon={<PlusIcon className="h-4 w-4" />}
                onClick={addMuestra}
              >
                Agregar Muestra
              </Button>
                </div>

            {/* Selector de muestra activa como pestañas */}
            <div className="flex flex-wrap gap-1.5" aria-label="Tabs">
              {muestras.map((_, index) => {
                const isActive = activeMuestraIndex === index;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setActiveMuestraIndex(index)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none
                      ${
                        isActive
                          ? 'bg-brand-50 text-brand-800 border border-brand-200'
                          : 'text-slate-600 border border-transparent hover:bg-slate-100 hover:text-slate-800'
                      }`}
                  >
                    Muestra {index + 1}
                  </button>
                );
              })}
              </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mt-4">
              {/* Sección de identificación de la muestra */}
              <div className="sm:col-span-2">
                {/* Responsable toma de muestra */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-slate-900 mb-4">Responsable Toma de Muestra</h4>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <FormField label="Nombre del responsable" required>
                      <Input
                        type="text"
                        name="responsable"
                        value={muestras[activeMuestraIndex].responsable}
                        onChange={handleMuestraChange}
                        required
                      />
                    </FormField>

                    <FormField label="Pertenece a" required>
                      <Select
                        name="pertenencia"
                        value={muestras[activeMuestraIndex].pertenencia}
                        onChange={handleMuestraChange}
                        required
                      >
                        <option value="">Seleccionar pertenencia</option>
                        <option value="cliente">Cliente</option>
                        <option value="laboratorio">Laboratorio</option>
                      </Select>
                    </FormField>
                  </div>
                  </div>

                {/* Identificación de la muestra */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-slate-900 mb-4">Identificación de la Muestra</h4>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <FormField label="ID de Origen">
                      <Input
                        type="text"
                        name="id_m_origen"
                        value={muestras[activeMuestraIndex].id_m_origen}
                        onChange={handleMuestraChange}
                        placeholder="Opcional"
                      />
                    </FormField>

                    <FormField label="ID de Laboratorio">
                      <Input
                        type="text"
                        name="id_m_lab"
                        value={muestras[activeMuestraIndex].id_m_lab}
                        onChange={handleMuestraChange}
                        placeholder="Opcional"
                      />
                    </FormField>
                  </div>
                  </div>

                {/* Identificación sitio de muestreo */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-slate-900 mb-4">Identificación Sitio de Muestreo</h4>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <FormField label="Sitio de Muestreo" required>
                      <Input
                        type="text"
                        name="sitio_muestreo"
                        value={muestras[activeMuestraIndex].sitio_muestreo}
                        onChange={handleMuestraChange}
                        placeholder="Ingrese el sitio de muestreo"
                        required
                      />
                    </FormField>

                    <FormField label="Latitud">
                      <Input
                        type="text"
                        name="georr_lat"
                        value={muestras[activeMuestraIndex].georr_lat}
                        onChange={handleMuestraChange}
                        placeholder="Ingrese la latitud"
                      />
                    </FormField>

                    <FormField label="Longitud">
                      <Input
                        type="text"
                        name="georr_long"
                        value={muestras[activeMuestraIndex].georr_long}
                        onChange={handleMuestraChange}
                        placeholder="Ingrese la longitud"
                      />
                    </FormField>
                  </div>
                </div>

                {/* Medio */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-slate-900 mb-4">Medio</h4>
                  <div className="grid grid-cols-1 gap-6">
                    {/* Un selector de pocas palabras no necesita todo el ancho. */}
                    <FormField label="Medio" required className="max-w-xs">
                      <Select
                        name="medio"
                        value={muestras[activeMuestraIndex].medio}
                        onChange={handleMuestraChange}
                        required
                      >
                        <option value="">Seleccionar medio</option>
                        {medios.map(medio => (
                          <option key={medio.id} value={medio.id}>
                            {medio.medio}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                  </div>
                  </div>

                {/* Fecha de muestreo */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-slate-900 mb-4">Fecha de Muestreo</h4>
                  <div className="grid grid-cols-1 gap-6">
                    <FormField label="Fecha de Muestreo" required className="max-w-[14rem]">
                      <Input
                        type="date"
                        name="fecha_muestreo"
                        value={muestras[activeMuestraIndex].fecha_muestreo}
                        onChange={handleMuestraChange}
                        required
                      />
                    </FormField>
                  </div>
                  </div>

                <div className="space-y-4">
                  <Checkbox
                    label="Organismo de Control"
                    checked={muestras[activeMuestraIndex].mostrar_organismo_control}
                    onChange={(e) => {
                      const newMuestras = [...muestras];
                      newMuestras[activeMuestraIndex] = {
                        ...muestras[activeMuestraIndex],
                        mostrar_organismo_control: e.target.checked,
                        organismo_control_id: e.target.checked ? null : muestras[activeMuestraIndex].organismo_control_id
                      };
                      setMuestras(newMuestras);
                    }}
                  />
                  {muestras[activeMuestraIndex].mostrar_organismo_control && (
                    <div>
                      <Select
                        value={muestras[activeMuestraIndex].organismo_control_id || ''}
                        onChange={(e) => {
                          const newMuestras = [...muestras];
                          newMuestras[activeMuestraIndex] = {
                            ...muestras[activeMuestraIndex],
                            organismo_control_id: e.target.value ? Number(e.target.value) : null
                          };
                          setMuestras(newMuestras);
                        }}
                        required={muestras[activeMuestraIndex].mostrar_organismo_control}
                      >
                        <option value="">Seleccionar organismo de control</option>
                        {organismosControl.map(org => (
                          <option key={org.id} value={org.id}>
                            {org.organismo}
                          </option>
                        ))}
                      </Select>
                      {organismosControlLoading && <span className="text-sm text-slate-500 mt-1">Cargando organismos...</span>}
                    </div>
                  )}

                  <Checkbox
                    label="Cadena de Custodia"
                    checked={muestras[activeMuestraIndex].mostrar_cadena_custodia}
                    onChange={(e) => {
                      const newMuestras = [...muestras];
                      newMuestras[activeMuestraIndex] = {
                        ...muestras[activeMuestraIndex],
                        mostrar_cadena_custodia: e.target.checked,
                        cadena_custodia: e.target.checked ? '' : muestras[activeMuestraIndex].cadena_custodia
                      };
                      setMuestras(newMuestras);
                    }}
                  />
                  {muestras[activeMuestraIndex].mostrar_cadena_custodia && (
                    <div>
                      <Input
                        type="text"
                        value={muestras[activeMuestraIndex].cadena_custodia}
                        onChange={(e) => {
                          const newMuestras = [...muestras];
                          newMuestras[activeMuestraIndex] = {
                            ...muestras[activeMuestraIndex],
                            cadena_custodia: e.target.value
                          };
                          setMuestras(newMuestras);
                        }}
                        placeholder="Ingrese la cadena de custodia"
                        required={muestras[activeMuestraIndex].mostrar_cadena_custodia}
                      />
                    </div>
                  )}
                </div>
                  </div>

                  {/* Sección de ensayos */}
              <div className="sm:col-span-2">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-medium text-slate-900">Ensayos</h4>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      icon={<TrashIcon className="h-4 w-4" />}
                      onClick={removeEnsayo}
                      disabled={muestras[activeMuestraIndex].ensayos.length <= 1}
                    >
                      Eliminar Ensayo
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      icon={<PlusIcon className="h-4 w-4" />}
                      onClick={addEnsayo}
                    >
                      Agregar Ensayo
                    </Button>
                    </div>
                </div>
                <div className="space-y-2">
                          {muestras[activeMuestraIndex].ensayos.map((ensayo, index) => (
                    <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-5 border border-slate-200 rounded-md p-2">
                      <FormField label={<span className="text-xs">Analito *</span>}>
                        <Input type="text" value={ensayo.analito} readOnly className="text-sm py-1 bg-slate-100" />
                      </FormField>
                      <FormField label={<span className="text-xs">Unidad *</span>}>
                        <Input type="text" value={ensayo.unidad} readOnly className="text-sm py-1 bg-slate-100" />
                      </FormField>
                      <FormField label={<span className="text-xs">Medio *</span>}>
                        <Input type="text" value={ensayo.medio} readOnly className="text-sm py-1 bg-slate-100" />
                      </FormField>
                      <FormField label={<span className="text-xs">Tipo *</span>}>
                        <Input type="text" value={ensayo.tipo} readOnly className="text-sm py-1 bg-slate-100" />
                      </FormField>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          size="sm"
                          icon={<PencilSquareIcon className="h-4 w-4" />}
                          onClick={() => handleEditEnsayo(index)}
                        >
                          Editar
                        </Button>
                    </div>
                    {/* Bloque de detalles extendidos */}
                    <div className="sm:col-span-5 text-[11px] text-slate-600 mt-1">
                      {ensayo.regulado_id ? (
                        <div>
                          <span className="font-medium">Regulado:</span> Marco {ensayo.marco_nombre || ensayo.id_marco} | Min: {ensayo.valor_min ?? '0'} | Max: {ensayo.valor_max ?? '0'} | Ref: {ensayo.valor_ref ?? '0'} {ensayo.presencia ? `| ${ensayo.presencia}` : ''}
                        </div>
                      ) : (
                        <div><span className="font-medium">Regulado:</span> No</div>
                      )}
                      {ensayo.controlado_id ? (
                        <div>
                          <span className="font-medium">Controlado:</span> Org.: {ensayo.organismo_nombre || ensayo.id_organismo} | Método: {ensayo.metodo_deteccion || ''} | Límite: {ensayo.limite_deteccion ?? '0'}
                        </div>
                      ) : (
                        <div><span className="font-medium">Controlado:</span> No</div>
                      )}
                    </div>
                  </div>
                  ))}
                </div>
            </div>
          </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleTabChange(eligeLaboratorio ? 'laboratorio' : 'solicitante')}
                disabled={loading}
              >
                Anterior
              </Button>
            </div>
          </div>
        );

      default:
        return <div>Contenido en construcción...</div>;
    }
  };

  const volverAlPanel = () => {
    setAnalisisCreado(null);
    if (onNavigate) {
      onNavigate('dashboard');
      return;
    }
    window.location.href = `${process.env.PUBLIC_URL || ''}/`;
  };

  // Función para manejar el botón siguiente
  const handleNext = () => {
    if (activeTab === 'solicitante') {
      handleTabChange(eligeLaboratorio ? 'laboratorio' : 'muestra');
    } else if (activeTab === 'laboratorio') {
      handleTabChange('muestra');
    }
  };

  // Función para manejar el botón anterior
  const handleBack = () => {
    if (activeTab === 'muestra') {
      handleTabChange(eligeLaboratorio ? 'laboratorio' : 'solicitante');
    } else if (activeTab === 'laboratorio') {
      handleTabChange('solicitante');
    }
  };

  // Renderizar botones de navegación según la pestaña
  const renderNavigationButtons = () => {
    return (
        <div className="mt-8 flex justify-between">
        {activeTab === 'solicitante' ? (
          <div className="flex-1"></div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            icon={<ArrowLeftIcon className="h-4 w-4" />}
            onClick={handleBack}
            disabled={loading || !tienePermisoCrearAnalisis}
          >
            Anterior
          </Button>
        )}

        {activeTab === 'muestra' ? (
          <Button
            type="submit"
            size="lg"
            loading={loading}
            disabled={loading || !tienePermisoCrearAnalisis}
          >
            Guardar Análisis
          </Button>
        ) : (
          <Button
            type="button"
            size="lg"
            icon={<ArrowRightIcon className="h-4 w-4" />}
            onClick={handleNext}
            disabled={loading || !tienePermisoCrearAnalisis}
          >
            Siguiente
          </Button>
        )}
        </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-card border border-slate-200">
      <PageHeader title="Crear Nuevo Análisis" />

      {mensajePermiso}

      {message && (
        <div className={`p-4 mb-6 rounded-md ${
          message.type === 'success' ? 'bg-green-50 border-l-4 border-green-500 text-green-700' : 'bg-red-50 border-l-4 border-red-500 text-red-700'
        }`}>
          {message.text}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {renderTabs()}
        {renderTabContent()}
        {renderNavigationButtons()}
      </form>
      
      {/* Añadir el popup de búsqueda de ensayos */}
      <BusquedaEnsayoPopup
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        onSelectEnsayo={handleSelectEnsayo}
        ensayoIndex={editingEnsayoIndex}
        medioActual={muestras[activeMuestraIndex]?.medio || ''}
        medios={medios}
        puedeCrearEnsayo={puedeCrearEnsayo}
      />

      {/* Añadir el modal de confirmación */}
      {renderConfirmModal()}

      {/* Aviso de creación: al aceptar se vuelve al panel. */}
      {analisisCreado !== null && (
        <Modal
          title="Análisis creado"
          onClose={volverAlPanel}
          size="sm"
          footer={
            <div className="flex justify-end">
              <Button onClick={volverAlPanel}>Aceptar</Button>
            </div>
          }
        >
          <p className="text-sm text-slate-700">
            El análisis se creó correctamente{analisisCreado ? ` con el número ${analisisCreado}` : ''}.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default CrearAnalisis;
